import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import AdminSection from "../../../shared/components/AdminSection";
import Button from "../../../shared/components/Button";
import EmptyState from "../../../shared/components/EmptyState";
import EventActionButton from "../../../shared/components/EventActionButton";
import SearchInput from "../../../shared/components/SearchInput";
import Spinner from "../../../shared/components/Spinner";
import useBodyScrollLock from "../../../shared/hooks/useBodyScrollLock";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { normalizeSearchText } from "../../../shared/utils/searchText";
import type { ClubPayload } from "../../clubs/shared/types/club";
import AdminPanelHeader from "./AdminPanelHeader";
import {
  createAdminClub,
  deleteAdminClub,
  listAdminClubs,
  updateAdminClub,
  updateAdminClubStatus,
} from "../../clubs/shared/services/clubsService";
import type { Club } from "../../clubs/shared/types/club";
import "./AdminClubsPanel.css";

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

function ClubIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 21h18" />
      <path d="M5 21V8l7-5 7 5v13" />
      <path d="M9 21v-7h6v7" />
      <path d="M9 10h.01" />
      <path d="M15 10h.01" />
    </svg>
  );
}

function RequiredMarker() {
  return (
    <span className="admin-clubs__required-marker" aria-hidden="true">
      *
    </span>
  );
}

function normalizeZipCode(value: string) {
  return String(value || "").replace(/\D/g, "").slice(0, 8);
}

function formatZipCode(value: string) {
  const digits = normalizeZipCode(value);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

function normalizePhoneNumber(value: string) {
  return String(value || "").replace(/\D/g, "").slice(0, 11);
}

function formatPhoneNumber(value: string) {
  const digits = normalizePhoneNumber(value);
  if (!digits) return "";
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

const MAX_IMAGE_BYTES = 3 * 1024 * 1024;
const IMAGE_DATA_URL_RE =
  /^data:image\/(jpeg|jpg|png|webp);base64,([a-z0-9+/=]+)$/i;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const GOOGLE_MAPS_SCRIPT_ID = "tnp-google-maps-api";

declare global {
  interface Window {
    google?: any;
    __tnpGoogleMapsPromise?: Promise<void>;
  }
}

function loadGoogleMapsScript() {
  if (!GOOGLE_MAPS_API_KEY) return Promise.reject(new Error("missing_key"));
  if (window.google?.maps) return Promise.resolve();
  if (window.__tnpGoogleMapsPromise) return window.__tnpGoogleMapsPromise;

  window.__tnpGoogleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("maps")), {
        once: true,
      });
      return;
    }
    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      GOOGLE_MAPS_API_KEY,
    )}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("maps"));
    document.head.appendChild(script);
  });
  return window.__tnpGoogleMapsPromise;
}

async function lookupCoordinatesByAddress(parts: string[]) {
  const query = parts
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
  if (!query) return null;
  if (GOOGLE_MAPS_API_KEY) {
    try {
      await loadGoogleMapsScript();
      const geocoder = window.google?.maps
        ? new window.google.maps.Geocoder()
        : null;
      if (geocoder) {
        const googleCoordinates = await new Promise<{
          latitude: number;
          longitude: number;
        } | null>((resolve) => {
          geocoder.geocode(
            { address: query, componentRestrictions: { country: "BR" } },
            (results: any[], status: string) => {
              const location = results?.[0]?.geometry?.location;
              if (status !== "OK" || !location) {
                resolve(null);
                return;
              }
              resolve({
                latitude: location.lat(),
                longitude: location.lng(),
              });
            },
          );
        });
        if (googleCoordinates) return googleCoordinates;
      }
    } catch {
      // Mantem o cadastro funcional quando a chave do Google não estiver válida.
    }
  }
  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=br&q=${encodeURIComponent(query)}`,
  );
  if (!response.ok) return null;
  const rows = await response.json();
  const first = Array.isArray(rows) ? rows[0] : null;
  const latitude = Number(first?.lat);
  const longitude = Number(first?.lon);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
}

const EMPTY_FORM: ClubPayload = {
  name: "",
  logoUrl: "",
  coverImageUrl: "",
  description: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  number: "",
  complement: "",
  neighborhood: "",
  country: "",
  phone: "",
  whatsapp: "",
  email: "",
  websiteUrl: "",
  instagramUrl: "",
  bookingUrl: "",
  lancesUrl: "",
  lancesPlatformName: "",
  latitude: null,
  longitude: null,
  isActive: true,
};

const ADDRESS_FIELDS = new Set<keyof ClubPayload>([
  "address",
  "city",
  "state",
  "zipCode",
  "number",
  "complement",
  "neighborhood",
  "country",
]);

type Props = {
  token: string;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
};

function toPayload(club: Club): ClubPayload {
  return {
    name: club.name,
    logoUrl: club.logoUrl,
    coverImageUrl: club.coverImageUrl,
    description: club.description,
    address: club.address,
    city: club.city,
    state: club.state,
    zipCode: formatZipCode(club.zipCode),
    number: club.number,
    complement: club.complement,
    neighborhood: club.neighborhood,
    country: club.country,
    phone: formatPhoneNumber(club.phone),
    whatsapp: formatPhoneNumber(club.whatsapp),
    email: club.email,
    websiteUrl: club.websiteUrl,
    instagramUrl: club.instagramUrl,
    bookingUrl: club.bookingUrl,
    lancesUrl: club.lancesUrl,
    lancesPlatformName: club.lancesPlatformName,
    latitude: club.latitude,
    longitude: club.longitude,
    isActive: club.isActive,
  };
}

function readImageFile(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("file"));
    reader.readAsDataURL(file);
  });
}

function normalizeCoordinateValue(value: number | null) {
  if (!Number.isFinite(Number(value))) return null;
  return Number(Number(value).toFixed(6));
}

function getImageDataUrlBytes(value: string) {
  const match = String(value || "").match(IMAGE_DATA_URL_RE);
  if (!match) return null;
  return Math.ceil((match[2].length * 3) / 4);
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number) {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error("timeout")), timeoutMs);
    promise
      .then((value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });
}

function buildClubAddressParts(form: ClubPayload) {
  return [
    form.address,
    form.number,
    form.neighborhood,
    normalizeZipCode(form.zipCode || ""),
    form.city,
    form.state,
    form.country || "Brasil",
  ];
}

function matchesAdminClubSearch(club: Club, search: string) {
  const normalizedSearch = normalizeSearchText(search);
  if (!normalizedSearch) return true;
  const haystack = [club.name, club.city]
    .map((value) => normalizeSearchText(value))
    .join(" ");
  return haystack.includes(normalizedSearch);
}

export default function AdminClubsPanel({ token, onError, onSuccess }: Props) {
  const t = siteTexts.admin.clubs;
  const [clubs, setClubs] = useState<Club[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [selectedClubId, setSelectedClubId] = useState<number | null>(null);
  const [form, setForm] = useState<ClubPayload>(EMPTY_FORM);
  const [showFormModal, setShowFormModal] = useState(false);
  const [draftsByKey, setDraftsByKey] = useState<Record<string, ClubPayload>>({});
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const coverInputRef = useRef<HTMLInputElement | null>(null);
  useBodyScrollLock(showFormModal);

  const currentDraftKey = selectedClubId ? `club-${selectedClubId}` : "create";

  const setDraftForKey = useCallback((key: string, nextDraft: ClubPayload) => {
    setDraftsByKey((current) => ({
      ...current,
      [key]: nextDraft,
    }));
  }, []);

  const clearDraftForKey = useCallback((key: string) => {
    setDraftsByKey((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, []);

  const selectedClub = useMemo(
    () => clubs.find((club) => club.id === selectedClubId) || null,
    [clubs, selectedClubId],
  );

  const loadClubs = useCallback(() => {
    if (!token) return;
    setLoading(true);
    listAdminClubs(token, search)
      .then((response) => setClubs(response.clubs || []))
      .catch((error) =>
        onError(error instanceof Error ? error.message : t.loadError),
      )
      .finally(() => setLoading(false));
  }, [onError, search, t.loadError, token]);

  useEffect(() => {
    const timeout = window.setTimeout(loadClubs, 180);
    return () => window.clearTimeout(timeout);
  }, [loadClubs]);

  const setField = <K extends keyof ClubPayload>(key: K, value: ClubPayload[K]) => {
    setForm((current) => {
      const next: ClubPayload = { ...current, [key]: value };
      if (
        ADDRESS_FIELDS.has(key) &&
        String(current[key] ?? "") !== String(value ?? "")
      ) {
        next.latitude = null;
        next.longitude = null;
      }
      setDraftForKey(currentDraftKey, next);
      return next;
    });
  };

  const setImageField = async (
    event: ChangeEvent<HTMLInputElement>,
    key: "logoUrl" | "coverImageUrl",
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      onError(t.imageTooLargeError);
      event.target.value = "";
      return;
    }
    try {
      const dataUrl = await readImageFile(file);
      setField(key, dataUrl);
    } catch (_err) {
      onError(t.imageError);
    }
  };

  const lookupAddressByCep = async () => {
    const zipCode = normalizeZipCode(form.zipCode);
    if (zipCode.length !== 8) {
      onError(t.cepLookupInvalid);
      return;
    }
    setCepLoading(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${zipCode}/json/`);
      if (!response.ok) throw new Error("cep");
      const data = await response.json();
      if (data?.erro) throw new Error("cep");
      const nextAddress = String(data?.logradouro || form.address || "").trim();
      const nextNeighborhood = String(data?.bairro || form.neighborhood || "").trim();
      const nextCity = String(data?.localidade || form.city || "").trim();
      const nextState = String(data?.uf || form.state || "").trim().toUpperCase();
      const coordinates = await lookupCoordinatesByAddress([
        nextAddress,
        nextNeighborhood,
        nextCity,
        nextState,
        "Brasil",
      ]);
      setForm((current) => {
        const next = {
          ...current,
          zipCode: formatZipCode(zipCode),
          address: nextAddress || current.address,
          neighborhood: nextNeighborhood || current.neighborhood,
          city: nextCity || current.city,
          state: nextState || current.state,
          complement: current.complement || String(data?.complemento || "").trim(),
          latitude: coordinates?.latitude ?? current.latitude,
          longitude: coordinates?.longitude ?? current.longitude,
        };
        setDraftForKey(currentDraftKey, next);
        return next;
      });
      onSuccess(
        coordinates ? t.cepLookupSuccessWithCoordinates : t.cepLookupSuccess,
      );
    } catch (_error) {
      onError(t.cepLookupError);
    } finally {
      setCepLoading(false);
    }
  };

  const startCreate = () => {
    setSelectedClubId(null);
    setForm(draftsByKey.create || EMPTY_FORM);
    setShowFormModal(true);
  };

  const startEdit = (club: Club) => {
    setSelectedClubId(club.id);
    setForm(draftsByKey[`club-${club.id}`] || toPayload(club));
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (saving) return;
    setShowFormModal(false);
  };

  const saveClub = async (event: FormEvent) => {
    event.preventDefault();
    if (!token) return;
    const isExistingLogo =
      Boolean(selectedClub) &&
      form.logoUrl.trim() === String(selectedClub?.logoUrl || "").trim();
    const isExistingCover =
      Boolean(selectedClub) &&
      form.coverImageUrl.trim() ===
        String(selectedClub?.coverImageUrl || "").trim();
    if (
      !form.name.trim() ||
      !form.logoUrl.trim() ||
      !form.address.trim() ||
      !form.city.trim() ||
      !form.state.trim()
    ) {
      onError(t.requiredFieldsError);
      return;
    }
    if (
      (!isExistingLogo &&
        (getImageDataUrlBytes(form.logoUrl) ?? 0) > MAX_IMAGE_BYTES) ||
      (!isExistingCover &&
        (getImageDataUrlBytes(form.coverImageUrl) ?? 0) > MAX_IMAGE_BYTES)
    ) {
      onError(t.imageTooLargeError);
      return;
    }
    setSaving(true);
    const draftKey = currentDraftKey;
    const isEditing = Boolean(selectedClub);
    try {
      const shouldLookupCoordinates =
        form.latitude === null || form.longitude === null;
      const coordinates = shouldLookupCoordinates
        ? await withTimeout(
            lookupCoordinatesByAddress(buildClubAddressParts(form)).catch(
              () => null,
            ),
            1800,
          ).catch(() => null)
        : null;
      const payload: ClubPayload = {
        ...form,
        zipCode: normalizeZipCode(form.zipCode),
        latitude: normalizeCoordinateValue(coordinates?.latitude ?? form.latitude),
        longitude: normalizeCoordinateValue(coordinates?.longitude ?? form.longitude),
      };
      const response = selectedClub
        ? await updateAdminClub(token, selectedClub.id, payload)
        : await createAdminClub(token, payload);
      onSuccess(selectedClub ? t.updateSuccess : t.createSuccess);
      clearDraftForKey(draftKey);
      clearDraftForKey(`club-${response.club.id}`);
      setSelectedClubId(response.club.id);
      setForm(toPayload(response.club));
      setShowFormModal(false);
      if (!isEditing) {
        setForm(EMPTY_FORM);
        setSelectedClubId(null);
      }
      setClubs((current) => {
        const nextClub = response.club;
        const remaining = current.filter((item) => item.id !== nextClub.id);
        if (!matchesAdminClubSearch(nextClub, search)) {
          return remaining;
        }
        return [nextClub, ...remaining];
      });
    } catch (error) {
      onError(error instanceof Error ? error.message : t.saveError);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (club: Club) => {
    try {
      const response = await updateAdminClubStatus(token, club.id, !club.isActive);
      setClubs((current) =>
        current.map((item) => (item.id === club.id ? response.club : item)),
      );
      if (selectedClubId === club.id) setForm(toPayload(response.club));
      onSuccess(response.club.isActive ? t.activated : t.deactivated);
    } catch (error) {
      onError(error instanceof Error ? error.message : t.saveError);
    }
  };

  const removeClub = async (club: Club) => {
    if (!window.confirm(t.deleteConfirm)) return;
    try {
      await deleteAdminClub(token, club.id);
      setClubs((current) => current.filter((item) => item.id !== club.id));
      if (selectedClubId === club.id) {
        setSelectedClubId(null);
        setForm(EMPTY_FORM);
        setShowFormModal(false);
      }
      clearDraftForKey(`club-${club.id}`);
      onSuccess(t.deleteSuccess);
    } catch (error) {
      onError(error instanceof Error ? error.message : t.saveError);
    }
  };

  return (
    <AdminSection className="admin-users-dashboard admin-tournaments-dashboard admin-clubs">
      <AdminPanelHeader
        className="admin-tournaments-hero"
        icon={<ClubIcon />}
        title={t.title}
        description={t.subtitle}
        action={
          <EventActionButton
            variant="primary"
            className="admin-history-create-button admin-tournaments-create-button"
            onClick={startCreate}
            icon={<PlusIcon />}
            label={t.newClub}
          />
        }
      />

      <div className="admin-clubs__layout">
        <section className="admin-clubs__list" aria-label={t.listTitle}>
          <SearchInput
            className="admin-clubs__search"
            inputType="search"
            value={search}
            placeholder={t.searchPlaceholder}
            aria-label={t.searchPlaceholder}
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch("")}
          />
          {loading ? (
            <Spinner label={t.loading} />
          ) : clubs.length === 0 ? (
            <EmptyState
              className="admin-empty-state"
              icon={<ClubIcon />}
              title={t.empty}
            />
          ) : (
            <div className="admin-clubs__cards">
              {clubs.map((club) => (
                <article
                  className={`admin-clubs__card${
                    selectedClubId === club.id ? " selected" : ""
                  }`}
                  key={club.id}
                >
                  <button type="button" onClick={() => startEdit(club)}>
                    <img src={club.logoUrl} alt="" />
                    <span>
                      <strong>{club.name}</strong>
                      <small>
                        {club.city} / {club.state}
                      </small>
                    </span>
                  </button>
                  <div className="admin-clubs__card-actions">
                    <Button
                      type="button"
                      variant="pill"
                      href={`#/clubes/${club.slug}`}
                    >
                      {t.viewPublic}
                    </Button>
                    <Button
                      type="button"
                      variant="pill"
                      onClick={() => toggleStatus(club)}
                    >
                      {club.isActive ? t.deactivate : t.activate}
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => removeClub(club)}
                    >
                      {t.delete}
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      {showFormModal && (
        <div className="admin-clubs__modal" role="presentation">
          <button
            type="button"
            className="admin-clubs__modal-backdrop"
            aria-label={t.closeModal}
            onClick={closeFormModal}
          />
          <form
            className="admin-clubs__form admin-clubs__modal-panel"
            onSubmit={saveClub}
            role="dialog"
            aria-modal="true"
            aria-label={selectedClub ? t.editTitle : t.createTitle}
          >
            <div className="admin-clubs__form-header">
              <div>
                <h3>{selectedClub ? t.editTitle : t.createTitle}</h3>
                <span>{selectedClub ? selectedClub.name : t.newClub}</span>
              </div>
              <div className="admin-clubs__form-header-actions">
                <label className="admin-clubs__toggle">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(event) =>
                      setField("isActive", event.target.checked)
                    }
                  />
                  {t.active}
                </label>
                <Button
                  type="button"
                  variant="pill"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  {siteTexts.common.cancel}
                </Button>
              </div>
            </div>

          <div className="admin-clubs__preview">
            <button
              type="button"
              className="admin-clubs__preview-cover-button"
              onClick={() => coverInputRef.current?.click()}
              aria-label={t.fields.cover}
            >
              {form.coverImageUrl ? <img src={form.coverImageUrl} alt="" /> : <span />}
            </button>
            <button
              type="button"
              className="admin-clubs__preview-logo-button"
              onClick={() => logoInputRef.current?.click()}
              aria-label={t.fields.logo}
            >
              {form.logoUrl ? <img src={form.logoUrl} alt="" /> : <strong>{t.logo}</strong>}
            </button>
          </div>

          <div className="admin-clubs__grid">
            <label>
              <span>
                {t.fields.name}
                <RequiredMarker />
              </span>
              <input value={form.name} onChange={(event) => setField("name", event.target.value)} required />
            </label>
            <label className="admin-clubs__wide">
              <span>{t.fields.description}</span>
              <textarea value={form.description} onChange={(event) => setField("description", event.target.value)} />
            </label>
            <label className="admin-clubs__wide">
              <span>{t.fields.zipCode}</span>
              <div className="admin-clubs__cep-field">
                <input
                  value={form.zipCode}
                  inputMode="numeric"
                  maxLength={9}
                  placeholder="00000-000"
                  aria-busy={cepLoading}
                  onChange={(event) =>
                    setField("zipCode", formatZipCode(event.target.value))
                  }
                />
                <Button
                  type="button"
                  variant="pill"
                  onClick={lookupAddressByCep}
                  disabled={cepLoading}
                >
                  {cepLoading ? t.cepLookupLoading : t.cepLookup}
                </Button>
              </div>
            </label>
            <label>
              <span>
                {t.fields.city}
                <RequiredMarker />
              </span>
              <input value={form.city} onChange={(event) => setField("city", event.target.value)} required />
            </label>
            <label>
              <span>
                {t.fields.state}
                <RequiredMarker />
              </span>
              <input value={form.state} onChange={(event) => setField("state", event.target.value)} required />
            </label>
            <label className="admin-clubs__wide">
              <span>
                {t.fields.address}
                <RequiredMarker />
              </span>
              <input value={form.address} onChange={(event) => setField("address", event.target.value)} required />
            </label>
            <label>
              <span>{t.fields.number}</span>
              <input value={form.number} onChange={(event) => setField("number", event.target.value)} />
            </label>
            <label>
              <span>{t.fields.neighborhood}</span>
              <input value={form.neighborhood} onChange={(event) => setField("neighborhood", event.target.value)} />
            </label>
            <label>
              <span>{t.fields.complement}</span>
              <input value={form.complement} onChange={(event) => setField("complement", event.target.value)} />
            </label>
            <label>
              <span>{t.fields.phone}</span>
              <input
                value={form.phone}
                inputMode="tel"
                placeholder="(00) 00000-0000"
                onChange={(event) =>
                  setField("phone", formatPhoneNumber(event.target.value))
                }
              />
            </label>
            <label>
              <span>{t.fields.whatsapp}</span>
              <input
                value={form.whatsapp}
                inputMode="tel"
                placeholder="(00) 00000-0000"
                onChange={(event) =>
                  setField("whatsapp", formatPhoneNumber(event.target.value))
                }
              />
            </label>
            <label>
              <span>{t.fields.email}</span>
              <input type="email" value={form.email} onChange={(event) => setField("email", event.target.value)} />
            </label>
            <label>
              <span>{t.fields.site}</span>
              <input value={form.websiteUrl} onChange={(event) => setField("websiteUrl", event.target.value)} />
            </label>
            <label>
              <span>{t.fields.instagram}</span>
              <input value={form.instagramUrl} onChange={(event) => setField("instagramUrl", event.target.value)} />
            </label>
            <label>
              <span>{t.fields.bookingUrl}</span>
              <input value={form.bookingUrl} onChange={(event) => setField("bookingUrl", event.target.value)} />
            </label>
            <label>
              <span>{t.fields.lancesUrl}</span>
              <input value={form.lancesUrl} onChange={(event) => setField("lancesUrl", event.target.value)} />
            </label>
            <label>
              <span>{t.fields.lancesPlatformName}</span>
              <input value={form.lancesPlatformName} onChange={(event) => setField("lancesPlatformName", event.target.value)} />
            </label>
            <label className="admin-clubs__wide">
              <span>
                {t.fields.logo}
                <RequiredMarker />
              </span>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                aria-required="true"
                onChange={(event) => setImageField(event, "logoUrl")}
              />
              {form.logoUrl ? (
                <div className="admin-clubs__file-preview">
                  <img src={form.logoUrl} alt={t.fields.logo} />
                  <strong>{t.fields.logo}</strong>
                </div>
              ) : null}
            </label>
            <label className="admin-clubs__wide">
              <span>{t.fields.cover}</span>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(event) => setImageField(event, "coverImageUrl")}
              />
              {form.coverImageUrl ? (
                <div className="admin-clubs__file-preview admin-clubs__file-preview--cover">
                  <img src={form.coverImageUrl} alt={t.fields.cover} />
                  <strong>{t.fields.cover}</strong>
                </div>
              ) : null}
            </label>
          </div>

          <div className="admin-clubs__submit">
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? t.saving : t.save}
            </Button>
          </div>
          </form>
        </div>
      )}
    </AdminSection>
  );
}
